import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  createToolCategory,
  updateToolCategory,
  deleteToolCategory,
} from "../../../api/tools";
import { PMField } from "../../utils/PMField";
import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

const ToolCategoryDialog = ({
  open,
  onClose,
  category = null,
  categories = [],
  onCategoryCreated,
  onCategoryUpdated,
  onCategoryDeleted,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const sortedCategories = useMemo(() => {
    return [...(categories || [])].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "nb", {
        sensitivity: "base",
      })
    );
  }, [categories]);

  useEffect(() => {
    if (!open) return;
    if (category) {
      setEditingCategory(category);
    } else {
      setEditingCategory(null);
    }
  }, [open, category]);

  const initialValues = {
    name: editingCategory?.name || "",
    description: editingCategory?.description || "",
    active:
      editingCategory?.active !== undefined ? editingCategory.active : true,
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(
      t("tool.category.name") + " " + t("is required")
    ),
    description: Yup.string().nullable(),
  });

  const handleSubmit = async (values, helpers) => {
    setIsLoading(true);
    try {
      let result;
      if (editingCategory) {
        result = await updateToolCategory(editingCategory.id, values);
        toast.success(t("tool.category.updated"));
        onCategoryUpdated?.(result.data);
        setEditingCategory(result.data);
      } else {
        result = await createToolCategory(values);
        toast.success(t("tool.category.created"));
        onCategoryCreated?.(result.data);
        helpers.resetForm({
          values: {
            name: "",
            description: "",
            active: true,
          },
        });
      }
    } catch (error) {
      console.error("Error saving tool category:", error);
      const msg = error.response?.data?.errors;
      if (Array.isArray(msg)) {
        msg.forEach((m) => toast.error(m));
      } else {
        toast.error(t("Failed to save tool category"));
      }
    } finally {
      setIsLoading(false);
      helpers.setSubmitting(false);
    }
  };

  const handleDelete = async (cat) => {
    const toolsCount = cat.tools_count ?? 0;
    const confirmMsg =
      toolsCount > 0
        ? t("tool.category.delete_confirm_with_tools", { count: toolsCount })
        : t("tool.category.delete_confirm");
    if (!window.confirm(confirmMsg)) return;

    setIsLoading(true);
    try {
      await deleteToolCategory(cat.id);
      toast.success(t("tool.category.deleted"));
      onCategoryDeleted?.(cat.id);
      if (editingCategory?.id === cat.id) {
        setEditingCategory(null);
      }
    } catch (error) {
      console.error("Error deleting tool category:", error);
      toast.error(t("tool.category.delete_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const startNew = () => {
    setEditingCategory(null);
  };

  const startEdit = (cat) => {
    setEditingCategory(cat);
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title="tool.category.manage_title"
      size="5xl"
    >
      <div className="flex w-full min-w-0 flex-col gap-4 overflow-x-hidden md:flex-row md:items-start md:gap-6">
        <aside className="flex w-full shrink-0 flex-col border-b border-gray-200 pb-4 md:w-[260px] md:border-b-0 md:border-r md:pb-0 md:pr-4 lg:w-[280px]">
          <div className="mb-3 flex shrink-0 items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900">
              {t("tool.category.all_categories")}
            </h4>
            <button
              type="button"
              onClick={startNew}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-blue-600 bg-white px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
            >
              <PlusIcon className="h-4 w-4" />
              {t("tool.category.new_category")}
            </button>
          </div>
          <div className="space-y-1">
            {sortedCategories.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">
                {t("tool.category.no_categories")}
              </p>
            ) : (
              sortedCategories.map((cat) => {
                const isActiveRow = editingCategory?.id === cat.id;
                return (
                  <div
                    key={cat.id}
                    className={`rounded-lg border p-2.5 transition-colors ${
                      isActiveRow
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {cat.name}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <span
                            className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${
                              cat.active
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-200 text-gray-600"
                            }`}
                          >
                            {cat.active ? t("Active") : t("tool.status.inactive")}
                          </span>
                          {typeof cat.tools_count === "number" && (
                            <span className="text-[10px] text-gray-500">
                              {t("tool.category.tools_count", {
                                count: cat.tools_count,
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => startEdit(cat)}
                          disabled={isLoading}
                          className="p-1.5 rounded text-gray-600 hover:bg-white hover:text-blue-600"
                          title={t("Edit")}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(cat)}
                          disabled={isLoading}
                          className="p-1.5 rounded text-gray-600 hover:bg-white hover:text-red-600"
                          title={t("Delete")}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        <div className="min-w-0 flex-1 md:pt-0">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">
            {editingCategory
              ? t("tool.category.edit_category")
              : t("tool.category.add_category")}
          </h4>
          <Formik
            key={editingCategory?.id ?? "new"}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {(formik) => (
              <Form className="space-y-4">
                <PMField
                  name="name"
                  label={t("tool.category.name")}
                  type="text"
                  required
                  {...formik}
                />

                <PMField
                  name="description"
                  label={t("tool.category.description")}
                  multiline
                  rows={3}
                  required
                  {...formik}
                />

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formik.values.active}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    {t("Active")}
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {t("Close")}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || formik.isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {isLoading || formik.isSubmitting
                      ? editingCategory
                        ? t("Updating...")
                        : t("Creating...")
                      : editingCategory
                      ? t("Update Category")
                      : t("Create Category")}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </PMDialog>
  );
};

export default ToolCategoryDialog;
